import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Mail, Lock, Trash2, AlertTriangle, Loader2, CheckCircle2, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import BottomNav from '../components/BottomNav';

const AccountSettings = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const [deleteConfirmation, setDeleteConfirmation] = useState('');

  const handleChangePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('De nye adgangskoder matcher ikke');
      return;
    }
    
    if (passwordForm.newPassword.length < 6) {
      toast.error('Adgangskoden skal være mindst 6 tegn');
      return;
    }
    
    setPasswordLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Adgangskode ændret!');
      setShowPasswordDialog(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunne ikke ændre adgangskode');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'SLET MIN KONTO') {
      toast.error('Skriv "SLET MIN KONTO" for at bekræfte');
      return;
    }
    
    setDeleteLoading(true);
    try {
      await axios.delete(`${API}/account`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Din konto er blevet slettet');
      logout();
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunne ikke slette konto');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <span className="text-lg font-display font-semibold text-foreground">Kontoindstillinger</span>
        </div>
      </div>

      <div className="pt-20 px-4">
        <div className="max-w-lg mx-auto">
          {/* Account Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card rounded-2xl p-6 mb-6 border border-border"
          >
            <h3 className="font-semibold text-foreground mb-4">Kontoinformation</h3>
            
            <div className="space-y-4">
              <div>
                <Label className="text-xs text-muted-foreground">Email</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  <span className="text-foreground">{user?.email}</span>
                  {user?.email_verified && (
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                  )}
                </div>
              </div>
              
              <div>
                <Label className="text-xs text-muted-foreground">Konto oprettet</Label>
                <p className="text-foreground mt-1">
                  {user?.created_at ? new Date(user.created_at).toLocaleDateString('da-DK', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  }) : '-'}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Security */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-card rounded-2xl border border-border mb-6 overflow-hidden"
          >
            <div className="p-4 border-b border-border">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" />
                <h3 className="font-semibold text-foreground">Sikkerhed</h3>
              </div>
            </div>
            
            {/* Change Password */}
            <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
              <DialogTrigger asChild>
                <button className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors">
                  <div className="flex items-center gap-3">
                    <Lock className="w-5 h-5 text-muted-foreground" />
                    <span className="font-medium text-foreground">Skift adgangskode</span>
                  </div>
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-sm rounded-2xl">
                <DialogHeader>
                  <DialogTitle className="font-display">Skift adgangskode</DialogTitle>
                  <DialogDescription>
                    Indtast din nuværende adgangskode og en ny adgangskode.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div>
                    <Label htmlFor="current-password">Nuværende adgangskode</Label>
                    <Input
                      id="current-password"
                      type="password"
                      value={passwordForm.currentPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                      className="mt-1 rounded-xl"
                    />
                  </div>
                  <div>
                    <Label htmlFor="new-password">Ny adgangskode</Label>
                    <Input
                      id="new-password"
                      type="password"
                      value={passwordForm.newPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                      className="mt-1 rounded-xl"
                    />
                  </div>
                  <div>
                    <Label htmlFor="confirm-password">Bekræft ny adgangskode</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      value={passwordForm.confirmPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                      className="mt-1 rounded-xl"
                    />
                  </div>
                  <Button
                    onClick={handleChangePassword}
                    disabled={passwordLoading}
                    className="w-full rounded-full"
                  >
                    {passwordLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Skift adgangskode'
                    )}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </motion.div>

          {/* Danger Zone */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-destructive/5 rounded-2xl border border-destructive/20 p-6"
          >
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              <h3 className="font-semibold text-destructive">Farezone</h3>
            </div>
            
            <p className="text-sm text-muted-foreground mb-4">
              Slet din konto permanent. Denne handling kan ikke fortrydes.
            </p>
            
            <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
              <DialogTrigger asChild>
                <Button variant="destructive" className="w-full rounded-full">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Slet min konto
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-sm rounded-2xl">
                <DialogHeader>
                  <DialogTitle className="font-display text-destructive">Slet konto?</DialogTitle>
                  <DialogDescription>
                    Er du sikker på, at du vil slette din konto? Alle dine data, matches, og beskeder vil blive slettet permanent.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="bg-destructive/10 p-3 rounded-xl">
                    <p className="text-sm text-destructive font-medium">
                      Skriv "SLET MIN KONTO" for at bekræfte:
                    </p>
                    <Input
                      value={deleteConfirmation}
                      onChange={(e) => setDeleteConfirmation(e.target.value)}
                      placeholder="SLET MIN KONTO"
                      className="mt-2 rounded-xl border-destructive/30"
                    />
                  </div>
                  <Button
                    onClick={handleDeleteAccount}
                    disabled={deleteLoading || deleteConfirmation !== 'SLET MIN KONTO'}
                    variant="destructive"
                    className="w-full rounded-full"
                  >
                    {deleteLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Slet min konto permanent'
                    )}
                  </Button>
                  <Button
                    onClick={() => {
                      setShowDeleteDialog(false);
                      setDeleteConfirmation('');
                    }}
                    variant="outline"
                    className="w-full rounded-full"
                  >
                    Annuller
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </motion.div>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default AccountSettings;
